import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal, flushSync } from "react-dom";
import {
  Alert,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import { Printer } from "lucide-react";
import { generateRequestUri } from "../../common/service";
import { AppContext } from "../../context";
import {
  closePackage,
  createShipmentPackage,
  getOpsShipmentParts,
  getOpsShipments,
  packIntoPackage,
  unpackPackage,
} from "../production/service";
import { getSignedToken } from "../service";
import { printPackageStickerLabel } from "../utils/printPackageStickerLabel";
import {
  panelStickerText,
  printPanelStickerLabels,
} from "../utils/printPanelStickerLabel";
import { toErrorText } from "./storeSalesDashboardHelpers";

const SCAN_BUFFER_GAP_MS = 100;
const PANEL_PACK_SCAN_PREFIX = "P-";

const SHIPMENT_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "dispatched", label: "Dispatched" },
  { value: "delivered", label: "Delivered" },
];

const SHIPMENT_STATUSES_BY_FILTER = {
  draft: ["draft"],
  dispatched: ["dispatched"],
  delivered: ["delivered"],
};

const PARTS_TAB_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "packed", label: "Packed" },
  { value: "loaded", label: "Loaded" },
];

const PENDING_PRODUCTION_FILTER_OPTIONS = [
  { value: "all", label: "ALL" },
  { value: "jit", label: "JIT" },
  { value: "pm", label: "PM" },
];

const formatDeliveryDate = (value) => {
  const t = Date.parse(value);

  return new Date(t).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const projectDisplayLabel = (project) => {
  const suffix = project.internal_id != null ? `#${project.internal_id}` : "";

  return project.name ? `${project.name} ${suffix}` : suffix;
};

const shipmentDisplayLabel = (shipment) => {
  const projectLabel = projectDisplayLabel(shipment.project);

  return shipment.name ? `${projectLabel} — ${shipment.name}` : projectLabel;
};

const panelDisplayLabel = (panel) => {
  const cabinet = panel.project_item_label;
  const component = panel.component;
  const label = panel.label;

  return `#${cabinet} - ${component}/${label}`;
};

const sortPanelsInStockFirst = (panels) =>
  [...panels].sort((a, b) => {
    if (Boolean(a.in_stock) === Boolean(b.in_stock)) return 0;

    return a.in_stock ? -1 : 1;
  });

const parsePackScan = (text) => {
  const trimmed = String(text ?? "").trim();
  if (!trimmed.startsWith(PANEL_PACK_SCAN_PREFIX)) return null;

  const partItemId = trimmed.slice(PANEL_PACK_SCAN_PREFIX.length).trim();
  if (!/^\d+$/.test(partItemId)) return null;

  return { partItemId };
};

const partItemInPackingGroups = (packingGroups, partItemId) =>
  Object.values(packingGroups).some((panels) =>
    panels.some((panel) => panel.part_item_id === Number(partItemId))
  );

const packingGroupsToSections = (
  packingGroups,
  showInStock,
  priorityGroupName = null
) => {
  const entries = Object.entries(packingGroups);
  const sortedEntries =
    priorityGroupName != null
      ? [...entries].sort(([nameA], [nameB]) => {
          if (nameA === priorityGroupName) return -1;
          if (nameB === priorityGroupName) return 1;

          return String(nameA).localeCompare(String(nameB));
        })
      : entries;

  return sortedEntries.map(([name, panels]) => ({
    key: name,
    title: name,
    panels: showInStock ? sortPanelsInStockFirst(panels) : panels,
  }));
};

const filterSectionsByProductionType = (sections, productionFilter) => {
  if (productionFilter === "all") return sections;

  return sections
    .map((section) => ({
      ...section,
      panels: section.panels.filter(
        (panel) => panel.production_type === productionFilter
      ),
    }))
    .filter((section) => section.panels.length > 0);
};

const collectPmPanelsFromPackingGroups = (packingGroups) =>
  Object.values(packingGroups ?? {})
    .flat()
    .filter((panel) => panel.production_type === "pm");

const collectAllPmPanelsFromShipmentParts = (shipmentParts) => {
  const pending = collectPmPanelsFromPackingGroups(
    shipmentParts.packing_groups
  );
  const packed = shipmentParts.packages
    .flatMap((pkg) => pkg.parts)
    .filter((panel) => panel.production_type === "pm");

  return [...pending, ...packed];
};

const packagesToSections = (packages) =>
  packages.map((pkg) => {
    const firstPart = pkg.parts[0];
    const title = firstPart
      ? `${pkg.label} - ${firstPart.packing_group}`
      : pkg.label;

    return {
      key: pkg.label,
      title,
      packageId: pkg.id,
      packageInternalId: pkg.internal_id,
      packageLabel: pkg.label,
      panels: pkg.parts,
    };
  });

const PanelSections = ({
  sections,
  showInStock,
  showPackagePrint,
  onPrintPackage,
  packingInProgress,
}) => {
  if (sections.length === 0) return null;

  return sections.map((section) => (
    <div key={section.key} className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="fw-semibold mb-0">{section.title}</h6>
        {showPackagePrint && section.packageId != null && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-secondary"
            aria-label="Print package QR"
            disabled={packingInProgress}
            onClick={() => onPrintPackage(section)}
          >
            <Printer size={18} />
          </button>
        )}
      </div>
      <div className="d-flex flex-wrap gap-2">
        {section.panels.map((panel, index) => {
          const inStock = showInStock && panel.in_stock;

          return (
            <div
              key={`${panel.project_item_label}-${panel.component}-${panel.label}-${index}`}
              className={`d-flex justify-content-center align-items-center rounded border shadow-sm flex-shrink-0${
                inStock
                  ? " bg-success-subtle border-success-subtle"
                  : " bg-white"
              }`}
              style={{ width: "120px" }}
            >
              <div className="font-monospace fw-bold py-2 d-flex flex-column align-items-center">
                <span>{panelDisplayLabel(panel)}</span>
                {panel.m_label && (
                  <span className="text-muted">{panel.m_label}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ));
};

const StoreOpsShipmentsSection = () => {
  const { currentUser } = useContext(AppContext);
  const showTestScanInput = true;
  const [statusFilter, setStatusFilter] = useState("draft");
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentParts, setShipmentParts] = useState(null);
  const [activePartsTab, setActivePartsTab] = useState("pending");
  const [pendingProductionFilter, setPendingProductionFilter] = useState("all");
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsError, setPartsError] = useState(null);
  const [packingInProgress, setPackingInProgress] = useState(false);
  const [drillingDownloadInProgress, setDrillingDownloadInProgress] =
    useState(false);
  const [packMessage, setPackMessage] = useState(null);
  const [packageQrPrint, setPackageQrPrint] = useState(null);
  const [panelStickerPrint, setPanelStickerPrint] = useState(null);
  const [scanText, setScanText] = useState("");
  const scanBufferRef = useRef("");
  const lastScanKeyTimeRef = useRef(0);
  const packingInProgressRef = useRef(false);

  const loadShipments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getOpsShipments();
      setShipments(rows);
    } catch (err) {
      setShipments([]);
      setError(toErrorText(err, "Unable to load shipments."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadParts = useCallback(async (shipmentId) => {
    if (!shipmentId) return;

    setPartsLoading(true);
    setPartsError(null);
    try {
      const payload = await getOpsShipmentParts(shipmentId);
      setShipmentParts(payload);
    } catch (err) {
      setShipmentParts(null);
      setPartsError(toErrorText(err, "Unable to load shipment parts."));
    } finally {
      setPartsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShipments();
  }, [loadShipments]);

  useEffect(() => {
    if (!selectedShipment) {
      setShipmentParts(null);
      setActivePartsTab("pending");
      setPendingProductionFilter("all");
      setPartsError(null);

      return;
    }

    setActivePartsTab("pending");
    setPendingProductionFilter("all");
    loadParts(selectedShipment.id);
  }, [selectedShipment, loadParts]);

  const filteredShipments = useMemo(() => {
    const statuses = SHIPMENT_STATUSES_BY_FILTER[statusFilter] ?? [
      statusFilter,
    ];

    return shipments.filter((shipment) => statuses.includes(shipment.status));
  }, [shipments, statusFilter]);

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        statusFilter === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [statusFilter]
  );

  const pendingProductionPillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        pendingProductionFilter === value
          ? "btn-primary"
          : "btn-light border text-dark"
      }`,
    [pendingProductionFilter]
  );

  const closePartsModal = () => {
    setSelectedShipment(null);
    setPackMessage(null);
    setScanText("");
    setPendingProductionFilter("all");
    scanBufferRef.current = "";
  };

  const runPackScan = useCallback(
    async (scanText) => {
      const parsed = parsePackScan(scanText);
      if (!parsed) {
        setPackMessage({
          type: "warning",
          message: "Invalid scan format.",
        });

        return;
      }

      if (!selectedShipment) return;

      if (!shipmentParts) return;

      if (
        !partItemInPackingGroups(
          shipmentParts.packing_groups,
          parsed.partItemId
        )
      ) {
        setPackMessage({
          type: "warning",
          message: `Part item #${parsed.partItemId} is not in pending packing groups for this shipment.`,
        });

        return;
      }

      const shipmentId = selectedShipment.id;

      if (packingInProgressRef.current) return;

      packingInProgressRef.current = true;
      setPackingInProgress(true);
      setPackMessage(null);

      try {
        const openPkg = shipmentParts
          ? shipmentParts.packages.find((pkg) => pkg.status === "open")
          : null;

        if (openPkg) {
          await packIntoPackage(openPkg.id, {
            part_item_id: parsed.partItemId,
          });
        } else {
          await createShipmentPackage(shipmentId, {
            part_item_id: parsed.partItemId,
          });
        }

        await loadParts(shipmentId);
        setPackMessage({
          type: "success",
          message: `Packed part item #${parsed.partItemId}.`,
        });
      } catch (err) {
        const message = toErrorText(err, "Unable to pack part.");
        setPackMessage({ type: "danger", message });
      } finally {
        packingInProgressRef.current = false;
        setPackingInProgress(false);
      }
    },
    [loadParts, selectedShipment, shipmentParts]
  );

  const runShipmentScan = useCallback(
    async (scanText) => {
      const trimmed = String(scanText ?? "").trim();

      if (trimmed.startsWith(PANEL_PACK_SCAN_PREFIX)) {
        await runPackScan(scanText);

        return;
      }

      setPackMessage({
        type: "warning",
        message: "Invalid scan format.",
      });
    },
    [runPackScan]
  );

  const handleScanSubmit = useCallback(() => {
    const text = scanText.trim();
    if (!text) return;

    runShipmentScan(text);
    setScanText("");
  }, [runShipmentScan, scanText]);

  useEffect(() => {
    if (!selectedShipment) return undefined;

    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable
      ) {
        return;
      }

      if (event.key === "Enter") {
        const buffered = scanBufferRef.current.trim();
        scanBufferRef.current = "";
        if (buffered) {
          event.preventDefault();
          runShipmentScan(buffered);
        }

        return;
      }

      if (event.key.length !== 1) return;

      const now = Date.now();
      if (now - lastScanKeyTimeRef.current > SCAN_BUFFER_GAP_MS) {
        scanBufferRef.current = "";
      }
      scanBufferRef.current += event.key;
      lastScanKeyTimeRef.current = now;
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [runShipmentScan, selectedShipment]);

  const pendingSections = useMemo(() => {
    if (!shipmentParts) return [];

    const openPkg = shipmentParts.packages.find((pkg) => pkg.status === "open");
    const priorityGroupName = openPkg?.parts?.[0]?.packing_group ?? null;

    return filterSectionsByProductionType(
      packingGroupsToSections(
        shipmentParts.packing_groups,
        true,
        priorityGroupName
      ),
      pendingProductionFilter
    );
  }, [shipmentParts, pendingProductionFilter]);

  const openPackageSection = useMemo(() => {
    if (!shipmentParts) return null;

    const openPkg = shipmentParts.packages.find((pkg) => pkg.status === "open");
    if (!openPkg) return null;

    const firstPart = openPkg.parts[0];
    const title = firstPart
      ? `${openPkg.label} - ${firstPart.packing_group}`
      : openPkg.label;

    return {
      id: openPkg.id,
      key: openPkg.label,
      title,
      packageId: openPkg.id,
      packageInternalId: openPkg.internal_id,
      packageLabel: openPkg.label,
      panels: sortPanelsInStockFirst(openPkg.parts),
    };
  }, [shipmentParts]);

  const runPackageAction = useCallback(
    async (action, successMessage) => {
      if (!openPackageSection || !selectedShipment) return;

      const packageId = openPackageSection.id;
      const shipmentId = selectedShipment.id;

      if (packingInProgressRef.current) return;

      packingInProgressRef.current = true;
      setPackingInProgress(true);
      setPackMessage(null);

      try {
        await action(packageId);
        await loadParts(shipmentId);
        setPackMessage({ type: "success", message: successMessage });
      } catch (err) {
        setPackMessage({
          type: "danger",
          message: toErrorText(err, "Unable to update package."),
        });
      } finally {
        packingInProgressRef.current = false;
        setPackingInProgress(false);
      }
    },
    [loadParts, openPackageSection, selectedShipment]
  );

  const handleClosePackage = useCallback(() => {
    runPackageAction(closePackage, "Package closed.");
  }, [runPackageAction]);

  const handleUnpackPackage = useCallback(() => {
    runPackageAction(unpackPackage, "Package unpacked.");
  }, [runPackageAction]);

  const packedSections = useMemo(() => {
    if (!shipmentParts) return [];

    return packagesToSections(
      shipmentParts.packages.filter((pkg) => pkg.status === "closed")
    );
  }, [shipmentParts]);

  const loadedSections = useMemo(() => {
    if (!shipmentParts) return [];

    return packagesToSections(
      shipmentParts.packages.filter((pkg) => pkg.status === "loaded")
    );
  }, [shipmentParts]);

  const pendingPmPanelCount = useMemo(() => {
    if (!shipmentParts) return 0;

    return collectPmPanelsFromPackingGroups(shipmentParts.packing_groups)
      .length;
  }, [shipmentParts]);

  const allPmPanelCount = useMemo(() => {
    if (!shipmentParts) return 0;

    return collectAllPmPanelsFromShipmentParts(shipmentParts).length;
  }, [shipmentParts]);

  const handleDownloadPmDrilling = useCallback(async () => {
    if (!selectedShipment || allPmPanelCount === 0) return;

    setDrillingDownloadInProgress(true);
    try {
      const { token } = await getSignedToken({
        userId: currentUser.id,
        resource: "shipment_pm_drilling",
      });
      window.open(
        generateRequestUri({
          url: `/store/shipments/${selectedShipment.id}/pm_drilling`,
          params: { signed: token },
        }),
        "_blank",
        "noreferrer"
      );
    } catch (err) {
      setPackMessage({
        type: "danger",
        message: toErrorText(err, "Unable to download drilling."),
      });
    } finally {
      setDrillingDownloadInProgress(false);
    }
  }, [allPmPanelCount, currentUser.id, selectedShipment]);

  const handlePrintPmStickers = useCallback(() => {
    if (!selectedShipment || !shipmentParts) return;

    const pmPanels = collectPmPanelsFromPackingGroups(
      shipmentParts.packing_groups
    );

    if (pmPanels.length === 0) {
      setPackMessage({
        type: "warning",
        message: "No PM panels to print.",
      });

      return;
    }

    const stickers = pmPanels.map((panel) =>
      panelStickerText(panel, selectedShipment.project.order_identifier)
    );

    printPanelStickerLabels({
      stickers,
      onReady: (data) => {
        flushSync(() => setPanelStickerPrint(data));
      },
      onDone: () => setPanelStickerPrint(null),
    }).catch(() => {
      setPanelStickerPrint(null);
      document.documentElement.classList.remove("panel-sticker-printing");
      document.body.classList.remove("panel-sticker-printing");
      document.getElementById("panel-sticker-print-page-style")?.remove();
    });
  }, [selectedShipment, shipmentParts]);

  const handlePrintPackage = useCallback(
    (section) => {
      if (!selectedShipment) return;

      printPackageStickerLabel({
        label: section.packageLabel,
        id: section.packageId,
        projectOrderIdentifier: selectedShipment.project.order_identifier,
        parts: section.panels,
        packageNo: section.packageInternalId,
        onReady: (data) => {
          flushSync(() => setPackageQrPrint(data));
        },
        onDone: () => setPackageQrPrint(null),
      }).catch(() => {
        setPackageQrPrint(null);
        document.documentElement.classList.remove("package-qr-printing");
        document.body.classList.remove("package-qr-printing");
        document.getElementById("package-qr-print-page-style")?.remove();
      });
    },
    [selectedShipment]
  );

  const modalTitle = selectedShipment
    ? shipmentDisplayLabel(selectedShipment)
    : "Shipment parts";

  const activeSections =
    activePartsTab === "packed"
      ? packedSections
      : activePartsTab === "loaded"
      ? loadedSections
      : pendingSections;
  const showInStock = activePartsTab === "pending";
  const loadedPackageCount = loadedSections.length;
  const closedOrLoadedPackageCount = packedSections.length + loadedPackageCount;
  const emptyMessage =
    activePartsTab === "packed"
      ? "No packed packages found for this shipment."
      : activePartsTab === "loaded"
      ? "No loaded packages found for this shipment."
      : "No pending panels found for this shipment.";

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <span className="text-muted small me-2">Status:</span>
        {SHIPMENT_STATUS_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={pillClassName(option.value)}
            onClick={() => setStatusFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && (
        <Alert color="danger" fade={false}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="py-5 text-center text-muted">
          <Spinner className="me-2" />
          Loading…
        </div>
      ) : filteredShipments.length === 0 ? (
        <p className="text-muted mb-0">No shipments found.</p>
      ) : (
        <div className="row row-cols-1 row-cols-md-3 g-3">
          {filteredShipments.map((shipment) => (
            <div key={shipment.id} className="col">
              <Card
                className="h-100 shadow-sm"
                role="button"
                onClick={() => setSelectedShipment(shipment)}
              >
                <CardBody>
                  <div className="text-dark fw-semibold">
                    {shipmentDisplayLabel(shipment)}
                  </div>
                  <div className="text-muted small mt-2">
                    <div className="text-capitalize">
                      Status: {shipment.status}
                    </div>
                    <div>
                      Delivery: {formatDeliveryDate(shipment.delivery_date)}
                    </div>
                    <div>Total panels: {shipment.panel_counts.total}</div>
                    <div>Packed: {shipment.panel_counts.packed}</div>
                    <div>In stock: {shipment.panel_counts.in_stock}</div>
                    <div>
                      Not in stock: {shipment.panel_counts.not_in_stock}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={selectedShipment != null}
        toggle={closePartsModal}
        fullscreen
      >
        <ModalHeader toggle={closePartsModal}>
          <h6 className="m-0">{modalTitle}</h6>
        </ModalHeader>
        <ModalBody>
          {packMessage && (
            <Alert color={packMessage.type} fade={false} className="mb-3">
              {packMessage.message}
            </Alert>
          )}

          {selectedShipment && shipmentParts && !partsLoading && (
            <div className="mb-3">
              {showTestScanInput && (
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div className="flex-grow-1">
                    <label
                      htmlFor="shipment-scan"
                      className="form-label small text-muted mb-1"
                    >
                      Scan
                    </label>
                    <input
                      id="shipment-scan"
                      type="text"
                      className="form-control form-control-sm font-monospace"
                      value={scanText}
                      disabled={packingInProgress}
                      onChange={(event) => setScanText(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleScanSubmit();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={packingInProgress || !scanText.trim()}
                    onClick={handleScanSubmit}
                  >
                    Submit
                  </button>
                </div>
              )}
              {closedOrLoadedPackageCount > 0 && (
                <div className="fw-bold mt-2 fs-6">
                  {loadedPackageCount} packages loaded out of{" "}
                  {closedOrLoadedPackageCount}
                </div>
              )}
            </div>
          )}

          {packingInProgress && (
            <div className="text-muted small mb-3 d-flex align-items-center">
              <Spinner size="sm" className="me-2" />
              Packing scanned part…
            </div>
          )}

          {partsLoading && (
            <div className="py-5 text-center text-muted">
              <Spinner className="me-2" />
              Loading…
            </div>
          )}

          {!partsLoading && partsError && (
            <Alert color="danger" fade={false}>
              {partsError}
            </Alert>
          )}

          {!partsLoading && !partsError && shipmentParts == null && (
            <p className="text-muted mb-0">No parts found for this shipment.</p>
          )}

          {!partsLoading && !partsError && shipmentParts != null && (
            <div className="row g-4">
              <div
                className={`m-0 ${openPackageSection ? "col-lg-8" : "col-12"}`}
              >
                <Nav tabs className="mb-3">
                  {PARTS_TAB_OPTIONS.map((option) => (
                    <NavItem key={option.value}>
                      <NavLink
                        href="#"
                        className={
                          activePartsTab === option.value ? "active" : ""
                        }
                        onClick={(event) => {
                          event.preventDefault();
                          setActivePartsTab(option.value);
                        }}
                      >
                        {option.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                {activePartsTab === "pending" && (
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {PENDING_PRODUCTION_FILTER_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={pendingProductionPillClassName(
                            option.value
                          )}
                          onClick={() =>
                            setPendingProductionFilter(option.value)
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={
                          packingInProgress ||
                          drillingDownloadInProgress ||
                          allPmPanelCount === 0
                        }
                        onClick={handleDownloadPmDrilling}
                      >
                        Download PM Minifix Drilling
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={
                          packingInProgress || pendingPmPanelCount === 0
                        }
                        onClick={handlePrintPmStickers}
                      >
                        Print PM Stickers
                      </button>
                    </div>
                  </div>
                )}

                {activeSections.length === 0 ? (
                  <p className="text-muted mb-0">{emptyMessage}</p>
                ) : (
                  <PanelSections
                    sections={activeSections}
                    showInStock={showInStock}
                    showPackagePrint
                    onPrintPackage={handlePrintPackage}
                    packingInProgress={packingInProgress}
                  />
                )}
              </div>

              {openPackageSection && (
                <div
                  className="col-lg-4 border-top border-lg-0 pt-4 pt-lg-0"
                  style={{ background: "#DDD" }}
                >
                  <hr className="d-lg-none my-0 mb-3" />
                  <div className="border-top d-none d-lg-block mb-3" />
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                    <h6 className="fw-semibold mb-0">Open package</h6>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      disabled={packingInProgress}
                      onClick={handleUnpackPackage}
                    >
                      Discard Package
                    </button>
                  </div>
                  {openPackageSection.panels.length === 0 ? (
                    <p className="text-muted mb-0">No parts in open package.</p>
                  ) : (
                    <PanelSections
                      sections={[openPackageSection]}
                      showInStock
                      showPackagePrint
                      onPrintPackage={handlePrintPackage}
                      packingInProgress={packingInProgress}
                    />
                  )}
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={packingInProgress}
                      onClick={handleClosePackage}
                    >
                      Close Package
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
      </Modal>

      {createPortal(
        <div id="package-qr-print-sheet" aria-hidden="true">
          {packageQrPrint &&
            packageQrPrint.pages.map((page, pageIndex) => (
              <div key={pageIndex} className="package-sticker-page">
                {pageIndex === 0 && (
                  <>
                    <div className="package-sticker-header">
                      <img
                        id="package-qr-print-image"
                        src={packageQrPrint.dataUrl}
                        alt=""
                      />
                      <div className="package-sticker-project">
                        {packageQrPrint.projectOrderIdentifier ?? "—"}
                      </div>
                    </div>
                    <hr className="package-sticker-divider" />
                  </>
                )}
                <div className="package-sticker-summary">
                  <span className="fw-bold fs-6">
                    Package no: {packageQrPrint.packageNo}
                  </span>
                  <span>Items: {packageQrPrint.itemCount}</span>
                </div>
                <hr className="package-sticker-divider" />
                <table
                  className="package-sticker-table"
                  style={{ fontSize: "11px" }}
                >
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "40%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Cabinet No</th>
                      <th>Part Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.parts.map((part, index) => (
                      <tr
                        key={`${part.project_item_id}-${part.component}-${part.label}-${pageIndex}-${index}`}
                      >
                        <td>{part.room_name || "—"}</td>
                        <td>{part.project_item_label}</td>
                        <td>{part.panel_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>,
        document.body
      )}

      {createPortal(
        <div id="panel-sticker-print-sheet" aria-hidden="true">
          {panelStickerPrint?.stickers?.map((sticker, index) => (
            <div key={index} className="panel-sticker-page">
              <div className="panel-sticker-print-caption">
                <div className="d-flex justify-content-between align-items-end">
                  <div>
                    <span className="fw-bold">
                      {sticker.projectLine.heading}
                    </span>{" "}
                    <span>{sticker.projectLine.value}</span>
                  </div>
                  <div className="d-flex align-items-end gap-2">
                    {sticker.m_label && (
                      <span className="fw-bold">{sticker.m_label}</span>
                    )}
                    {sticker.qrDataUrl && (
                      <img src={sticker.qrDataUrl} alt="" />
                    )}
                  </div>
                </div>
                <hr className="panel-sticker-divider" />
                {sticker.detailLines.map((line) => (
                  <div key={line.heading} className="panel-sticker-line">
                    <span className="fw-bold">{line.heading}</span>{" "}
                    <span className={line.valueClassName}>{line.value}</span>
                  </div>
                ))}
                {sticker.barcodeDataUrl && (
                  <div className="d-flex justify-content-center">
                    <img src={sticker.barcodeDataUrl} />
                  </div>
                )}
                {sticker.scanCode && (
                  <div className="text-center small text-muted fw-bold">
                    {sticker.scanCode}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default StoreOpsShipmentsSection;
